import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export type SessionStatus = 'idle' | 'active' | 'suspended' | 'revoked' | 'expired' | 'ended' | 'error'

export const useProfessionalSessionStore = defineStore(
  'professionalSession',
  () => {
    // State
    const professionalToken = ref<string | null>(null)
    const professionalUserId = ref<string | null>(null)
    const professionalName = ref<string>('')
    const professionalRole = ref<string>('')
    const observedParentUserId = ref<string | null>(null)
    const observedParentName = ref<string>('')
    const sessionStatus = ref<SessionStatus>('idle')
    const tokenExpiresAt = ref<number>(0)
    const warningThresholdMs = ref(
      Number(import.meta.env.VITE_SESSION_WARNING_THRESHOLD_MS) || 300_000
    )

    // Computed
    const isProfessionalSession = computed(
      () => professionalToken.value !== null && sessionStatus.value === 'active'
    )

    const timeRemainingMs = computed(() => {
      if (!tokenExpiresAt.value) return 0
      return Math.max(0, tokenExpiresAt.value - Date.now())
    })

    const isExpiringSoon = computed(
      () =>
        isProfessionalSession.value &&
        timeRemainingMs.value > 0 &&
        timeRemainingMs.value < warningThresholdMs.value
    )

    const isExpired = computed(
      () => isProfessionalSession.value && timeRemainingMs.value <= 0
    )

    // Actions
    async function initializeFromAuthCode(code: string, parentUserId: string) {
      try {
        // POC: Simulate exchanging auth code for session token
        // In production, this calls: POST /api/professional-access/auth/exchange
        // with { code, parentUserId } and gets back a session token + metadata
        console.log('[Observer POC] Exchanging auth code for session token', { code, parentUserId })

        // Simulated response
        professionalToken.value = `poc-token-${Date.now()}`
        professionalUserId.value = 'prof-user-123'
        professionalName.value = 'Jane Attorney'
        professionalRole.value = 'Attorney'
        observedParentUserId.value = parentUserId
        observedParentName.value = 'John Parent'
        sessionStatus.value = 'active'
        // Token expires in 30 minutes for POC
        tokenExpiresAt.value = Date.now() + 30 * 60 * 1000

        return true
      } catch {
        sessionStatus.value = 'error'
        return false
      }
    }

    async function refreshToken() {
      try {
        // POC: Simulate token refresh
        // In production: POST /api/professional-access/auth/refresh
        console.log('[Observer POC] Refreshing session token')
        professionalToken.value = `poc-token-${Date.now()}`
        tokenExpiresAt.value = Date.now() + 30 * 60 * 1000
        return true
      } catch {
        sessionStatus.value = 'expired'
        return false
      }
    }

    function endSession() {
      sessionStatus.value = 'ended'
      clearSession()
    }

    function clearSession() {
      professionalToken.value = null
      professionalUserId.value = null
      professionalName.value = ''
      professionalRole.value = ''
      observedParentUserId.value = null
      observedParentName.value = ''
      tokenExpiresAt.value = 0
    }

    return {
      // State
      professionalToken,
      professionalUserId,
      professionalName,
      professionalRole,
      observedParentUserId,
      observedParentName,
      sessionStatus,
      tokenExpiresAt,
      warningThresholdMs,
      // Computed
      isProfessionalSession,
      timeRemainingMs,
      isExpiringSoon,
      isExpired,
      // Actions
      initializeFromAuthCode,
      refreshToken,
      endSession,
      clearSession
    }
  },
  {
    persist: {
      key: 'professional-session',
      storage: sessionStorage
    }
  }
)
